import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { MaskedField } from '../../components/masking/MaskedField';
import { offersService, type Offer } from '../../services/offers.service';
import { useWizardStore } from '../../store/wizard.store';
import { formatSAR, formatDate, formatDateTime } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { ArrowLeft, Send, Clock, AlertCircle } from 'lucide-react';

const SENDABLE_STATUSES = ['Draft', 'In Progress', 'Sent', 'Accepted', 'Rejected'];
const sendButtonLabel = (status: string) => {
  if (status === 'Sent') return 'Resend to Customer';
  if (status === 'Accepted' || status === 'Rejected') return 'Resend Link';
  return 'Send to Customer';
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card">
    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
      <span className="section-title" style={{ marginBottom: 0 }}>{title}</span>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>{children}</div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; fullWidth?: boolean }> = ({ label, children, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <div style={{ fontSize: '11px', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '3px', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--text-1)' }}>{children}</div>
  </div>
);

export const OfferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { setOfferId } = useWizardStore();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    offersService.get(id).then(setOffer).catch(() => showToast('Failed to load offer', 'error')).finally(() => setLoading(false));
  }, [id, showToast]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AppNav title="Loading…" />
        <main style={{ flex: 1, padding: '24px' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card" style={{ padding: '24px', marginBottom: '16px' }}>
              <div className="skeleton" style={{ height: '14px', width: '40%', marginBottom: '16px' }} />
              <div className="skeleton" style={{ height: '80px' }} />
            </div>
          ))}
        </main>
      </div>
    );
  }

  if (!offer) return null;

  const isMasked = user?.role === 'reseller-agent' && ['Accepted', 'Rejected'].includes(offer.status);
  const isExpiredOffer = offer.status === 'Expired' ||
    (offer.status === 'Sent' && offer.customerLinkExpiresAt && new Date(offer.customerLinkExpiresAt).getTime() < Date.now());

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title={offer.offerNumber} subtitle={`Status: ${offer.status}`} />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge label={isExpiredOffer && offer.status !== 'Expired' ? 'Expired' : offer.status} />
              <span style={{ color: 'var(--text-2)', fontSize: '13px' }}>
                Created {formatDateTime(offer.createdAt)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-offers')}>
                <ArrowLeft size={14} />
                Back
              </Button>
              {(offer.status === 'Draft' || offer.status === 'In Progress') && (
                <Button variant="secondary" size="sm" onClick={() => {
                  setOfferId(offer.id, offer.offerNumber);
                  if (!offer.items?.length) navigate('/catalog');
                  else if (!offer.customer?.nameEn) navigate('/offers/wizard/cart');
                  else if (!offer.authorizedPerson?.nameEn) navigate('/offers/wizard/customer');
                  else navigate('/offers/wizard/summary');
                }}>
                  Continue Editing
                </Button>
              )}
              {SENDABLE_STATUSES.includes(offer.status) && (
                <Button size="sm" onClick={() => navigate(`/offers/${offer.id}/send`)}>
                  <Send size={14} />
                  {sendButtonLabel(offer.status)}
                </Button>
              )}
            </div>
          </div>

          {isMasked && (
            <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={14} />
              Some information is masked because this offer has been closed.
            </div>
          )}

          {isExpiredOffer && (
            <div style={{ background: '#FFF7E8', border: '1px solid var(--warn-border)', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={14} />
              This offer has expired. The customer link is no longer valid.
            </div>
          )}

          {/* Products */}
          <Section title="Products & Services">
            {(offer.items ?? []).length === 0 ? (
              <p style={{ color: 'var(--text-2)', fontSize: '14px' }}>No items.</p>
            ) : (
              <>
                {(offer.items ?? []).map((item) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{item.product?.name ?? 'Product'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>
                        {item.product?.category}
                        {item.location && <> · {item.location.addressLine1 ?? `${item.location.lat}, ${item.location.lng}`}</>}
                      </div>
                    </div>
                    <Badge label={item.status} />
                    <div style={{ fontWeight: 700, minWidth: '100px', textAlign: 'right', color: 'var(--text-1)' }}>
                      {formatSAR(item.price)}/mo
                    </div>
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 16px', marginTop: '12px', fontSize: '13.5px', maxWidth: '280px', marginLeft: 'auto' }}>
                  <span style={{ color: 'var(--text-2)' }}>Subtotal</span><span style={{ textAlign: 'right' }}>{formatSAR(offer.subtotal)}</span>
                  <span style={{ color: 'var(--text-2)' }}>VAT</span><span style={{ textAlign: 'right' }}>{formatSAR(offer.vatAmount)}</span>
                  <span style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-1)' }}>Total</span>
                  <span style={{ fontWeight: 800, fontSize: '15px', textAlign: 'right', color: 'var(--p)' }}>{formatSAR(offer.total)}</span>
                </div>
              </>
            )}
          </Section>

          {/* Customer */}
          {offer.customer && (
            <Section title="Customer Information">
              <Grid>
                <Field label="Company (EN)"><MaskedField value={offer.customer.nameEn} type="nameEn" offerStatus={offer.status} /></Field>
                <Field label="Company (AR)"><MaskedField value={offer.customer.nameAr} type="nameAr" offerStatus={offer.status} /></Field>
                <Field label="CR Number"><MaskedField value={offer.customer.crNumber} type="cr" offerStatus={offer.status} /></Field>
                <Field label="CR Expiry">{offer.customer.crExpiry ? formatDate(offer.customer.crExpiry) : '—'}</Field>
                {offer.customer.vatNumber && <Field label="VAT Number">{offer.customer.vatNumber}</Field>}
                {offer.customer.contactMobile && <Field label="Mobile"><MaskedField value={offer.customer.contactMobile} type="mobile" offerStatus={offer.status} /></Field>}
                {offer.customer.contactEmail && <Field label="Email">{offer.customer.contactEmail}</Field>}
                {offer.customer.landline && <Field label="Landline"><MaskedField value={offer.customer.landline} type="landline" offerStatus={offer.status} /></Field>}
                {offer.customer.addressEn && <Field label="Address (EN)" fullWidth>{isMasked ? <MaskedField value={offer.customer.addressEn} type="address" offerStatus={offer.status} /> : offer.customer.addressEn}</Field>}
              </Grid>
            </Section>
          )}

          {/* Authorized Person */}
          {offer.authorizedPerson && (
            <Section title="Authorized Person">
              <Grid>
                <Field label="Name (EN)"><MaskedField value={offer.authorizedPerson.nameEn} type="nameEn" offerStatus={offer.status} /></Field>
                <Field label="Name (AR)"><MaskedField value={offer.authorizedPerson.nameAr} type="nameAr" offerStatus={offer.status} /></Field>
                <Field label="Mobile"><MaskedField value={offer.authorizedPerson.mobile} type="mobile" offerStatus={offer.status} /></Field>
                <Field label="ID Type">{offer.authorizedPerson.idType}</Field>
                <Field label="ID Number"><MaskedField value={offer.authorizedPerson.idNumber} type="idNumber" offerStatus={offer.status} /></Field>
              </Grid>
            </Section>
          )}

          {/* Timeline */}
          <Section title="Timeline">
            <Grid>
              <Field label="Created">{formatDateTime(offer.createdAt)}</Field>
              {offer.sentAt && <Field label="Sent">{formatDateTime(offer.sentAt)}</Field>}
            </Grid>
            {offer.sentAt && offer.customerLinkExpiresAt && (() => {
              const ms = new Date(offer.customerLinkExpiresAt).getTime() - Date.now();
              const days = ms / (1000 * 60 * 60 * 24);
              const color = days < 1 ? 'var(--err)' : days <= 3 ? 'var(--warn)' : 'var(--succ)';
              const daysLabel = days < 0 ? 'Expired' : days < 1 ? 'Expires today' : `${Math.ceil(days)} day${Math.ceil(days) !== 1 ? 's' : ''} remaining`;
              return (
                <div style={{ display: 'flex', gap: '8px', fontSize: '13.5px', alignItems: 'center', marginTop: '12px' }}>
                  <span style={{ color: 'var(--text-2)', flexShrink: 0 }}>Link expires:</span>
                  <span style={{ fontWeight: 600 }}>{formatDate(offer.customerLinkExpiresAt)}</span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color, background: `${color}18`, padding: '2px 8px', borderRadius: '20px' }}>{daysLabel}</span>
                </div>
              );
            })()}
          </Section>
        </div>
      </main>
    </div>
  );
};
